import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateReview, Difficulty } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { ButtonGroupInputOption } from '@sib/ui';
import { z } from 'zod';

export const MAX_MESSAGE_LENGTH = 800;

export enum FeedbackFormField {
  GettingStartedEase = 'gettingStartedEase',
  NavigationEase = 'navigationEase',
  FeedbackDescription = 'feedbackDescription',
}

export const useValidationSchema = () => {
  const { t } = useTranslation();

  return z.object({
    [FeedbackFormField.GettingStartedEase]: z
      .union([z.nativeEnum(Difficulty), z.literal('')])
      .refine(value => value, {
        message: t('validation.required'),
      }),
    [FeedbackFormField.NavigationEase]: z
      .union([z.nativeEnum(Difficulty), z.literal('')])
      .refine(value => value, {
        message: t('validation.required'),
      }),
    [FeedbackFormField.FeedbackDescription]: z
      .string()
      .max(MAX_MESSAGE_LENGTH, {
        message: t('validation.messageCantBeLongerThan', {
          max: MAX_MESSAGE_LENGTH,
        }),
      })
      .optional(),
  });
};

type FeedbackFormValues = z.infer<ReturnType<typeof useValidationSchema>>;

const defaultValues: FeedbackFormValues = {
  [FeedbackFormField.GettingStartedEase]: '',
  [FeedbackFormField.NavigationEase]: '',
  [FeedbackFormField.FeedbackDescription]: '',
};

export const useFormProps = () => {
  const validationSchema = useValidationSchema();

  return useForm<FeedbackFormValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(validationSchema),
  });
};

interface UseOnSubmitProps {
  onSuccess: () => void;
}

export const useOnSubmit = ({ onSuccess }: UseOnSubmitProps) => {
  const { open } = useNotification();
  const { t } = useTranslation();

  const { mutateAsync: createReview, ...rest } = useCreateReview({
    onSuccess: () => {
      open?.({
        type: 'success',
        message: t('components.feedbackDialog.successMessage.message'),
        description: t('components.feedbackDialog.successMessage.description'),
      });
      onSuccess();
    },
    onError: error => {
      console.error(error);
      open?.({
        type: 'error',
        message: t('components.feedbackDialog.errorMessage.message'),
        description: t('components.feedbackDialog.errorMessage.description'),
      });
    },
  });
  const onSubmit = useCallback(
    async (values: FeedbackFormValues) => {
      try {
        if (
          !values[FeedbackFormField.GettingStartedEase] ||
          !values[FeedbackFormField.NavigationEase]
        )
          return;
        await createReview({
          gettingStarted: values[FeedbackFormField.GettingStartedEase],
          navigationEase: values[FeedbackFormField.NavigationEase],
          improvementSuggestion:
            values[FeedbackFormField.FeedbackDescription] || '',
        });
      } catch (e) {
        return e;
      }
    },
    [createReview]
  );

  return { ...rest, onSubmit };
};

export const useDifficultyOptions = () => {
  const { t } = useTranslation();

  const options: ButtonGroupInputOption[] = [
    Difficulty.VeryEasy,
    Difficulty.Easy,
    Difficulty.Neutral,
    Difficulty.Hard,
    Difficulty.VeryHard,
  ].map(difficulty => ({
    text: t(`enums.Difficulty.${difficulty}`),
    value: difficulty,
  }));

  return options;
};
